/**
 * Private Key Utilities for Hyperliquid Trading
 * 
 * SECURITY NOTE: Private keys are stored ONLY in browser localStorage, 
 * NEVER in the backend database for HYPERLIQUID portfolios.
 * They are passed via X-Agent-Private-Key header (not query params) for security.
 */

/**
 * Storage key formats used for private keys
 */
const STORAGE_KEY_FORMATS = {
  testnet: (portfolioId) => `hyperliquid_agent_testnet_${portfolioId}`,
  mainnet: (portfolioId) => `hyperliquid_agent_mainnet_${portfolioId}`,
  legacy: (portfolioId) => `agent_private_key_${portfolioId}`,
};

/**
 * Get the agent private key from localStorage for a given portfolio
 * 
 * @param {number|string} portfolioId - The portfolio ID
 * @returns {string|null} The private key if found, null otherwise
 */
export function getAgentPrivateKey(portfolioId) {
  if (!portfolioId) return null;
  
  const testnetKey = STORAGE_KEY_FORMATS.testnet(portfolioId);
  const mainnetKey = STORAGE_KEY_FORMATS.mainnet(portfolioId);
  const legacyKey = STORAGE_KEY_FORMATS.legacy(portfolioId);
  
  // Try testnet key first, then mainnet, then legacy format
  return localStorage.getItem(testnetKey) 
    || localStorage.getItem(mainnetKey)
    || localStorage.getItem(legacyKey);
}

/**
 * Store the agent private key in localStorage
 * 
 * @param {number|string} portfolioId - The portfolio ID
 * @param {string} privateKey - The private key to store
 * @param {boolean} isTestnet - Whether this is a testnet portfolio
 */
export function setAgentPrivateKey(portfolioId, privateKey, isTestnet = true) {
  if (!portfolioId || !privateKey) return;
  
  const storageKey = isTestnet 
    ? STORAGE_KEY_FORMATS.testnet(portfolioId)
    : STORAGE_KEY_FORMATS.mainnet(portfolioId);
    
  localStorage.setItem(storageKey, privateKey);
}

/**
 * Check if an agent private key exists in localStorage for a given portfolio
 *
 * @param {number|string} portfolioId - The portfolio ID
 * @returns {boolean}
 */
export function hasAgentPrivateKey(portfolioId) {
  return !!getAgentPrivateKey(portfolioId);
}

/**
 * Remove the agent private key from localStorage
 * 
 * @param {number|string} portfolioId - The portfolio ID
 */
export function removeAgentPrivateKey(portfolioId) {
  if (!portfolioId) return;
  
  // Remove all possible key formats
  Object.values(STORAGE_KEY_FORMATS).forEach(formatFn => {
    const key = formatFn(portfolioId);
    localStorage.removeItem(key);
  });
}

/**
 * Get headers object with X-Agent-Private-Key if private key is available
 * 
 * @param {number|string} portfolioId - The portfolio ID
 * @param {Object} additionalHeaders - Additional headers to include
 * @returns {Object} Headers object with X-Agent-Private-Key if available
 */
export function getPrivateKeyHeaders(portfolioId, additionalHeaders = {}) {
  const headers = {
    'accept': 'application/json',
    ...additionalHeaders,
  };
  
  const privateKey = getAgentPrivateKey(portfolioId);
  if (privateKey) {
    headers['X-Agent-Private-Key'] = privateKey;
  }
  
  return headers;
}

/**
 * Create a fetch wrapper that automatically includes private key header
 * 
 * @param {string} url - The URL to fetch
 * @param {number|string} portfolioId - The portfolio ID
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>} Fetch response
 */
export async function fetchWithPrivateKey(url, portfolioId, options = {}) {
  const headers = getPrivateKeyHeaders(portfolioId, options.headers || {});
  
  return fetch(url, {
    ...options,
    headers,
  });
}

export default {
  getAgentPrivateKey,
  hasAgentPrivateKey,
  setAgentPrivateKey,
  removeAgentPrivateKey,
  getPrivateKeyHeaders,
  fetchWithPrivateKey,
};
